import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  TextField,
  InputAdornment,
} from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import CancelButton from 'app/components/buttons/CancelButton'
import ResponsiveDialog from 'app/components/dialog/ResponsiveDialog'
import useMobileStyle from 'app/components/mobileModal/mobileModalStyle'
import { Search } from '@material-ui/icons'
import { useDispatch, useSelector } from 'react-redux'
import { closeAddSubscriber } from 'app/main/topics/store/actions/topic-action'
import * as R from 'ramda'
import { makeStyles } from '@material-ui/styles'
import effects from 'app/effects/effects'
import MessageAlert from 'app/components/MessageAlert'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import FetchTable, {
  useTableNotServerSide,
} from 'app/components/table/FetchTable'

const useStyles = makeStyles((theme) => ({
  groupDialogStyleOne: {
    '& .MuiPaper-root': {
      maxWidth: 422,
    },
  },
  groupDialogTitleWrap: {
    wordBreak: 'break-all',
  },
  addSubscriberTable: {
    marginTop: 16,
    '& .rt-tbody': {
      overflow: 'auto !important',
      maxHeight: 270,
    },
  },
  addSubscriberSearchNoData: {
    width: '100%',
    height: 200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addSubscriberNoData: {
    color: '#404040',
    marginTop: 16,
    marginBottom: 16,
  },
  addSubscriberHint: {
    fontSize: 14,
    color: '#a8a8a8',
    marginTop: 16,
    marginBottom: 16,
  },
  actionBtn: {
    backgroundColor: '#0073b1',
    '&:hover': {
      backgroundColor: '#0073b1',
      borderColor: '#0073b1',
    },
    '&:focus': {},
  },
  groupActionBtns: {
    padding: '8px 30px',
  },
  renameActionBtns: {
    paddingRight: 24,
  },
}))

export default ({ subscriberList, topics, isOpen, onChange }) => {
  const mobileClasses = useMobileStyle()
  const { formatMessage } = useIntl()
  const dispatch = useDispatch()
  const classes = useStyles()
  const { dialogState } = useSelector((state) => state.topicReducer)
  const [isLoading, setIsLoading] = useState(false)

  const {
    state: localState,
    checkBoxColumn,
    onPageChange,
    onPageSizeChange,
    setToDfaultData,
    onSearchData,
    submitData,
    setInitailDate,
  } = useTableNotServerSide()

  const isEmptyCheckIds = R.compose(
    R.equals(0),
    R.length,
    R.when(R.isNil, []),
    R.prop('checkedItems'),
  )(localState)

  const sortArray = (data) => {
    if (R.isNil(data)) {
      return false
    }
    return R.sort((a, b) => {
      return a - b
    }, data)
  }

  const isPristine = R.equals(
    sortArray(localState.checkedItems),
    sortArray(R.prop('subscriberIds', dialogState)),
  )

  const columns = [
    checkBoxColumn,
    {
      Header: formatMessage({ id: 'firstName.label' }),
      accessor: 'firstName',
      id: 'firstName',
      filterable: true,
      width: 120,
    },
    {
      Header: formatMessage({ id: 'lastName.label' }),
      accessor: 'lastName',
      id: 'lastName',
      filterable: true,
      width: 120,
    },
  ]

  const handleAddSubscriber = async (subscriberIds) => {
    try {
      await setIsLoading(true)
      await effects.apis.subscribersFollowTopics({
        topicsId: R.prop('id', topics),
        subscriberIds: subscriberIds,
      })
      await setIsLoading(false)
      await onChange()
      await submitData(subscriberIds)
      await dispatch(closeAddSubscriber())
    } catch (error) {
      await setIsLoading(false)
      dispatch(
        MessageAlert.showError({
          message: formatMessage({ id: 'error.general' }),
        }),
      )
    }
  }

  const handleSubmit = () => {
    handleAddSubscriber(localState.checkedItems)
  }

  const subscriberCount = R.compose(
    R.length,
    R.prop('subscriberIds'),
  )(dialogState)

  useEffect(() => {
    if (isOpen) {
      setInitailDate({
        checkedItems: R.prop('subscriberIds', dialogState),
        subscriberList,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, subscriberCount])

  return (
    <ResponsiveDialog
      fullWidth={false}
      isFullScreen={false}
      open={isOpen}
      className={clsx(
        classes.groupDialogStyleOne,
        mobileClasses.lessPaddingMobile,
      )}
    >
      <DialogTitle className={classes.groupDialogTitleWrap}>
        <FormattedMessage
          id="group.details.dialogs.addSubscriber"
          values={{
            name: R.prop('name', topics),
          }}
        />
      </DialogTitle>

      <DialogContent>
        {R.isEmpty(localState.subscriberList) && !localState.searchText ? (
          <div className={classes.addSubscriberNoData}>
            <p>{formatMessage({ id: 'group.notSubscribersToAdd' })}</p>
          </div>
        ) : (
          <div className="flex flex-col justify-center w-full">
            <FormControl>
              <TextField
                id="standard-search"
                margin="dense"
                placeholder={formatMessage({ id: 'search' })}
                value={localState.searchText}
                onChange={(e) =>
                  onSearchData({ text: e.target.value, subscriberList })
                }
                type="search"
                variant="outlined"
                autoFocus
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
            <FetchTable
              data={localState.subscriberList}
              total={localState.subscriberList.length}
              columns={columns}
              showPagination={localState.subscriberList.length > 10}
              sortable={true}
              resizable={true}
              minRows={1}
              defaultPageSize={10}
              className={classes.addSubscriberTable}
              onPageChange={onPageChange}
              noDataText={formatMessage({ id: 'group.notSubscribers' })}
              onPageSizeChange={onPageSizeChange}
            />
            <span className={classes.addSubscriberHint}>
              <FormattedMessage
                id="group.details.dialogs.selectedSubscriber"
                values={{
                  count: R.length(localState.checkedItems),
                }}
              />
            </span>
          </div>
        )}
      </DialogContent>

      <DialogActions className={classes.renameActionBtns}>
        <CancelButton
          onClick={() => {
            dispatch(closeAddSubscriber())
            setToDfaultData({
              checkIds: R.prop('subscriberIds', dialogState),
              subscriberList,
            })
          }}
          className={mobileClasses.modalBtn}
          isLoading={isLoading}
          disabled={isLoading}
        />
        <PrimaryButton
          onClick={handleSubmit}
          isLoading={isLoading}
          disabled={isEmptyCheckIds || isPristine || isLoading}
          className={clsx(
            classes.actionBtn,
            classes.groupActionBtns,
            mobileClasses.modalBtn,
          )}
          variant="contained"
          labelId="action.add"
        />
      </DialogActions>
    </ResponsiveDialog>
  )
}
